import React, { useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

type BoardType = 'roomonly' | 'breakfast' | 'halfboard' | 'fullboard' | 'allinclusive';

interface RateRuleFormData {
  name: string;
  match_type: 'prefix' | 'suffix' | 'exact' | 'regex';
  match_value: string;
  tier: string;
  rate_category: 'standard' | 'package';
  rate_type: 'net' | 'gross';
  board_type: BoardType | '';
  markup: string;
  commission: string;
  is_blocked: boolean;
  notes: string;
}

export interface RateRule {
  id: string;
  chain_id: string;
  name: string;
  match_type: 'prefix' | 'suffix' | 'exact' | 'regex';
  match_value: string;
  tier: string | null;
  rate_category: 'standard' | 'package';
  rate_type: 'net' | 'gross';
  board_type: BoardType | null;
  markup: number | null;
  commission: number | null;
  paired_rule_id: string | null;
  is_blocked: boolean;
  notes: string | null;
  is_active: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, unknown>) => void;
  existingRule?: RateRule | null;
  loading?: boolean;
}

const BOARD_TYPE_OPTIONS: Array<{ value: BoardType | ''; label: string }> = [
  { value: '', label: 'None (inherit from rate plan)' },
  { value: 'roomonly', label: 'Room only' },
  { value: 'breakfast', label: 'Breakfast (BB)' },
  { value: 'halfboard', label: 'Half board (HB)' },
  { value: 'fullboard', label: 'Full board (FB)' },
  { value: 'allinclusive', label: 'All inclusive (AI)' },
];

const defaultValues: RateRuleFormData = {
  name: '',
  match_type: 'suffix',
  match_value: '',
  tier: '',
  rate_category: 'standard',
  rate_type: 'net',
  board_type: '',
  markup: '',
  commission: '',
  is_blocked: false,
  notes: '',
};

export default function RateRuleModal({ open, onClose, onSubmit, existingRule, loading }: Props) {
  const { control, handleSubmit, reset, watch } = useForm<RateRuleFormData>({
    mode: 'onBlur',
    defaultValues,
  });

  const rateType = watch('rate_type');
  const isBlocked = watch('is_blocked');

  useEffect(() => {
    if (!open) return;
    if (existingRule) {
      reset({
        name: existingRule.name || '',
        match_type: existingRule.match_type || 'suffix',
        match_value: existingRule.match_value || '',
        tier: existingRule.tier || '',
        rate_category: existingRule.rate_category || 'standard',
        rate_type: existingRule.rate_type || 'net',
        board_type: existingRule.board_type || '',
        markup: existingRule.markup?.toString() || '',
        commission: existingRule.commission?.toString() || '',
        is_blocked: existingRule.is_blocked || false,
        notes: existingRule.notes || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [open, existingRule, reset]);

  const handleFormSubmit = (data: RateRuleFormData) => {
    onSubmit({
      name: data.name,
      match_type: data.match_type,
      match_value: data.match_value,
      tier: data.tier ? data.tier.toLowerCase() : null,
      rate_category: data.rate_category,
      rate_type: data.rate_type,
      board_type: data.board_type || null,
      markup: data.markup ? parseFloat(data.markup) : null,
      commission: data.commission ? parseFloat(data.commission) : null,
      is_blocked: data.is_blocked,
      notes: data.notes || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existingRule ? 'Edit Rate Rule' : 'Add Rate Rule'}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Rule Name"
                  placeholder="e.g. IHG Standard Rates"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  fullWidth
                />
              )}
            />

            <Stack direction="row" spacing={2}>
              <Controller
                name="match_type"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Match Type" fullWidth>
                    <MenuItem value="prefix">prefix</MenuItem>
                    <MenuItem value="suffix">suffix</MenuItem>
                    <MenuItem value="exact">exact</MenuItem>
                    <MenuItem value="regex">regex</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="match_value"
                control={control}
                rules={{ required: 'Match value is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Match Value"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Controller
              name="tier"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Tier"
                  placeholder="e.g. tier1, apac, au (leave blank for global)"
                  fullWidth
                  inputProps={{ maxLength: 20 }}
                  helperText="Optional — if set, this rule only applies when resolving rates for this tier. Stored as lowercase."
                />
              )}
            />

            <Stack direction="row" spacing={2}>
              <Controller
                name="rate_category"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Rate Category" fullWidth>
                    <MenuItem value="standard">standard</MenuItem>
                    <MenuItem value="package">package</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="rate_type"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Rate Type" fullWidth>
                    <MenuItem value="net">net</MenuItem>
                    <MenuItem value="gross">gross</MenuItem>
                  </TextField>
                )}
              />
            </Stack>

            <Controller
              name="board_type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Board Type"
                  fullWidth
                  helperText="Sets the meal-plan inclusion on new room rates created by cm-sync"
                >
                  {BOARD_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Stack direction="row" spacing={2}>
              <Controller
                name="markup"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isBlocked || rateType !== 'net') return true;
                    if (!value && value !== '0') return 'Markup is required for net rates';
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Markup %"
                    type="number"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'Applied to net rates'}
                    disabled={isBlocked || rateType === 'gross'}
                    fullWidth
                  />
                )}
              />
              <Controller
                name="commission"
                control={control}
                rules={{
                  validate: (value) => {
                    if (isBlocked || rateType !== 'gross') return true;
                    if (!value && value !== '0') return 'Commission is required for gross rates';
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Commission %"
                    type="number"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message || 'Applied to gross rates'}
                    disabled={isBlocked || rateType === 'net'}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Controller
              name="is_blocked"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={field.onChange} />}
                  label="Block this rate (prevents ingestion)"
                />
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  placeholder="Optional notes for the commercial team"
                  multiline
                  rows={2}
                  fullWidth
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {existingRule ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
